import { useCallback, useEffect, useState } from 'react';
import type { Conversation, Section } from '@volt/shared';
import { useSession } from '../state/session.js';
import { cacheConversations, readCachedConversations } from '../lib/db.js';
import { filterVisibleConversations } from '../lib/sidebarInteractions.js';
import { useOnline } from './useOnline.js';

export interface ConversationsState {
  conversations: Conversation[];
  loading: boolean;
  fromCache: boolean;
  reload: () => void;
}

/**
 * Conversation list for the sidebar. Results are cached in IndexedDB and served
 * from cache while offline or when the request fails, so history stays visible.
 */
export function useConversations(
  section: Section,
  search: string,
  archived: boolean,
  version: number,
): ConversationsState {
  const { api } = useSession();
  const online = useOnline();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromCache, setFromCache] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = search.trim();

    const serveCache = async () => {
      const cached = await readCachedConversations(section);
      if (cancelled) return;
      const filtered = query
        ? cached.filter((item) => item.title.toLowerCase().includes(query.toLowerCase()))
        : cached;
      setConversations(filterVisibleConversations(filtered, archived));
      setFromCache(true);
    };

    if (!online) {
      void serveCache().finally(() => {
        if (!cancelled) setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }

    api
      .listConversations({ section, search: query || undefined, archived })
      .then((response) => {
        if (cancelled) return;
        setConversations(filterVisibleConversations(response.conversations, archived));
        setFromCache(false);
        void cacheConversations(response.conversations);
      })
      .catch(() => serveCache())
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, section, search, archived, version, nonce, online]);

  return { conversations, loading, fromCache, reload };
}
